import ImageUploading from 'react-images-uploading';
import React, { useState, useRef } from 'react';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { Button, Typography, IconButton, Box } from '@material-ui/core';
import BackupIcon from '@material-ui/icons/Backup';
import DeleteIcon from '@material-ui/icons/Delete';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import PhotoLibraryOutlinedIcon from '@material-ui/icons/PhotoLibraryOutlined';

import ImageCard from './imageCard';
import { useWindowSize } from '../../../Core/Controls/ScreenResolution';
import DialogComp from '../../../Core/Modal/dialogModal';
import { PrimaryButton, SecondaryButton } from '../../../Core/FormInput';

const ImageUpload = (props) => {
    const { disabled, state, onImageChange, submitImage, onClose, maxNumber = 10, RemoveImage, isAPI, loader } = props;

    const showDialog = props.showDialog === undefined ? true : !!props.showDialog;

    const [windowWidth] = useWindowSize();
    let imageSizeLimit = { height: 800, width: 800 };
    const localRef = useRef(null);
    const fileInputRef = props.fileInputRef || localRef;
    const [activeImageIndex, setActiveImageIndex] = useState(0);

    const resizeImage = (image, maxWidth, maxHeight) => {
        return new Promise((resolve) => {
            imageSizeLimit = { height: maxHeight, width: maxWidth };
            const img = new Image();
            img.src = image.data_url || image;

            const canvas = document.createElement('canvas');
            document.body.append(canvas);
            canvas.style.display = 'none';
            const ctx = canvas.getContext('2d');
            img.onload = () => {
                let ch = img.height;
                let cw = img.width;

                if (ch > imageSizeLimit.height || cw > imageSizeLimit.width) {
                    if (ch > cw) {
                        const ratio = imageSizeLimit.height / ch;
                        cw = cw * ratio;
                        ch = imageSizeLimit.height;
                    } else {
                        const ratio = imageSizeLimit.width / cw;
                        ch = ratio * cw;
                        ch = imageSizeLimit.height;
                    }
                }
                if (ch > imageSizeLimit.height) {
                    const ratio = imageSizeLimit.height / ch;
                    cw = cw * ratio;
                    ch = imageSizeLimit.height;
                }
                if (cw > imageSizeLimit.width) {
                    const ratio = imageSizeLimit.width / cw;
                    ch = ch * ratio;
                    cw = imageSizeLimit.width;
                }

                canvas.height = ch;
                canvas.width = cw;
                ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, cw, ch);

                const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
                canvas.remove();
                resolve(typeof image === 'object' ? { ...image, data_url: dataUrl } : dataUrl);
            };
            img.onerror = () => {
                canvas.remove();
                resolve(image);
            };
        });
    };

    const onCompressedImageChange = (imageList) => {
        const resizedImages = imageList.map((image) => {
            if (image.data_url) {
                return resizeImage(image, 800, 800);
            } else {
                return image;
            }
        });

        Promise.all(resizedImages).then((newImages) => {
            onImageChange(newImages);
        });
    };

    const scrollNext = () => {
        const slider = document.getElementById('thumbnail-scroll-container');
        if (slider) slider.scrollBy({ left: 120, top: 0, behavior: 'smooth' });
    };

    const scrollPrev = () => {
        const slider = document.getElementById('thumbnail-scroll-container');
        if (slider) slider.scrollBy({ left: -120, top: 0, behavior: 'smooth' });
    };

    const handleImageChange = (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const currentImages = state.selectedImages || [];
        const array = [...currentImages];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    array.push({
                        data_url: reader.result,
                        file: file
                    });
                    onCompressedImageChange(array);
                };
                reader.readAsDataURL(file);
            }
        }
    };

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const TitleComp = ({ count }) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <PhotoLibraryOutlinedIcon style={{ color: '#f50057', fontSize: 22 }} />
                    <span style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>Inspection Photos</span>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', background: '#f1f5f9', padding: '4px 10px', borderRadius: 12 }}>
                    {count} {count === 1 ? 'Photo' : 'Photos'}
                </div>
            </div>
        );
    };

    const formattedImages = (state.selectedImages || []).map((img) => {
        if (typeof img === 'string') {
            return { data_url: img };
        }
        if (img && img.data_url) {
            return img;
        }
        return { data_url: img };
    });

    return (
        <ImageUploading multiple value={formattedImages} onChange={onCompressedImageChange} dataURLKey="data_url">
            {({ imageList, onImageRemove }) => {
                const safeIndex = activeImageIndex >= imageList.length ? Math.max(0, imageList.length - 1) : activeImageIndex;
                const activeItem = imageList[safeIndex];
                const activeUrl = activeItem?.data_url || activeItem;

                return (
                    <>
                        {/* Hidden file input */}
                        <input
                            type="file"
                            accept="image/*, application/pdf"
                            onChange={handleImageChange}
                            id="file-input"
                            multiple
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                        />
                        {showDialog && (
                            <DialogComp
                                title={<TitleComp count={imageList.length} />}
                                onClose={onClose}
                                maxWidth="md"
                                fullWidth
                            >
                                <div style={{ padding: '10px 20px 16px 20px' }}>
                                    {/* Top Controls Bar */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <div style={{ fontSize: 12, color: '#64748b' }}>
                                    {imageList.length > 0
                                        ? 'Select a thumbnail below to view full-size photo'
                                        : 'Upload clear photos of the check item or defect'}
                                </div>
                                {!disabled && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={handleButtonClick}
                                        startIcon={<BackupIcon />}
                                        style={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: 12.5,
                                            padding: '5px 14px',
                                            borderRadius: 6,
                                            backgroundColor: '#0284c7',
                                            boxShadow: '0 1px 3px rgba(2, 132, 199, 0.3)'
                                        }}
                                    >
                                        Add Photos
                                    </Button>
                                )}
                            </div>

                            {/* Main Display Area (Fixed Height Portrait Mode) */}
                            {imageList.length > 0 ? (
                                <div
                                    style={{
                                        position: 'relative',
                                        width: '100%',
                                        height: 440,
                                        background: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        padding: 8,
                                        boxSizing: 'border-box',
                                        marginBottom: 14
                                    }}
                                >
                                    <div
                                        style={{
                                            position: 'relative',
                                            height: '100%',
                                            width: '100%',
                                            maxWidth: 380,
                                            background: '#ffffff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: 6,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            padding: 4,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        <img
                                            src={activeUrl}
                                            alt={`Inspection photo ${safeIndex + 1}`}
                                            style={{
                                                maxHeight: '100%',
                                                maxWidth: '100%',
                                                width: 'auto',
                                                height: 'auto',
                                                objectFit: 'contain',
                                                display: 'block'
                                            }}
                                        />
                                    </div>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            bottom: 12,
                                            right: 14,
                                            background: 'rgba(15, 23, 42, 0.75)',
                                            color: '#fff',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            padding: '3px 8px',
                                            borderRadius: 4,
                                            backdropFilter: 'blur(4px)'
                                        }}
                                    >
                                        {safeIndex + 1} of {imageList.length}
                                    </div>
                                </div>
                            ) : (
                                <div
                                    onClick={!disabled ? handleButtonClick : undefined}
                                    style={{
                                        width: '100%',
                                        height: 440,
                                        background: '#f8fafc',
                                        border: '2px dashed #cbd5e1',
                                        borderRadius: 8,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: !disabled ? 'pointer' : 'default',
                                        marginBottom: 14,
                                        boxSizing: 'border-box',
                                        transition: 'all 0.2s ease',
                                        padding: 20
                                    }}
                                >
                                    <CloudUploadOutlinedIcon style={{ fontSize: 54, color: '#94a3b8', marginBottom: 10 }} />
                                    <Typography style={{ fontWeight: 600, fontSize: 14, color: '#334155', marginBottom: 4 }}>
                                        No photos uploaded yet
                                    </Typography>
                                    <Typography style={{ fontSize: 12, color: '#64748b', textAlign: 'center', maxWidth: 360 }}>
                                        {!disabled
                                            ? 'Click here or click "Add Photos" to browse and upload inspection photos (PNG, JPG)'
                                            : 'No images available for this check item'}
                                    </Typography>
                                </div>
                            )}

                            {/* Thumbnail Carousel Strip */}
                            {imageList.length > 0 && (
                                <div
                                    style={{
                                        position: 'relative',
                                        display: 'flex',
                                        alignItems: 'center',
                                        background: '#f1f5f9',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 8,
                                        padding: '8px 12px',
                                        minHeight: 82,
                                        boxSizing: 'border-box',
                                        marginBottom: 16
                                    }}
                                >
                                    {imageList.length > 4 && (
                                        <IconButton
                                            size="small"
                                            onClick={scrollPrev}
                                            style={{
                                                backgroundColor: '#fff',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                marginRight: 6,
                                                padding: 4
                                            }}
                                        >
                                            <ChevronLeftIcon fontSize="small" />
                                        </IconButton>
                                    )}

                                    <div
                                        id="thumbnail-scroll-container"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            overflowX: 'auto',
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            flex: 1,
                                            padding: '4px 2px'
                                        }}
                                    >
                                        {imageList.map((im, i) => {
                                            const itemUrl = im.data_url || im;
                                            const isSelected = i === safeIndex;

                                            return (
                                                <div
                                                    key={i}
                                                    style={{
                                                        position: 'relative',
                                                        flexShrink: 0,
                                                        width: 64,
                                                        height: 64,
                                                        borderRadius: 6,
                                                        border: isSelected ? '2.5px solid #f50057' : '1px solid #cbd5e1',
                                                        boxShadow: isSelected ? '0 0 0 2px rgba(245, 0, 87, 0.2)' : '0 1px 2px rgba(0,0,0,0.05)',
                                                        cursor: 'pointer',
                                                        overflow: 'hidden',
                                                        background: '#fff',
                                                        transition: 'all 0.15s ease'
                                                    }}
                                                    onClick={() => setActiveImageIndex(i)}
                                                >
                                                    <img
                                                        src={itemUrl}
                                                        alt={`Thumb ${i + 1}`}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain',
                                                            display: 'block',
                                                            backgroundColor: '#f8fafc'
                                                        }}
                                                    />

                                                    {!disabled && !loader && (
                                                        <div
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isAPI) {
                                                                    RemoveImage?.(im, i);
                                                                } else {
                                                                    onImageRemove(i);
                                                                }
                                                                if (i <= safeIndex && safeIndex > 0) {
                                                                    setActiveImageIndex((prev) => Math.max(0, prev - 1));
                                                                }
                                                            }}
                                                            style={{
                                                                position: 'absolute',
                                                                top: 2,
                                                                right: 2,
                                                                width: 18,
                                                                height: 18,
                                                                borderRadius: '50%',
                                                                backgroundColor: '#ef4444',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: '#fff',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                                cursor: 'pointer',
                                                                zIndex: 10
                                                            }}
                                                            title="Delete photo"
                                                        >
                                                            <DeleteIcon style={{ fontSize: 12 }} />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {imageList.length > 4 && (
                                        <IconButton
                                            size="small"
                                            onClick={scrollNext}
                                            style={{
                                                backgroundColor: '#fff',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                marginLeft: 6,
                                                padding: 4
                                            }}
                                        >
                                            <ChevronRightIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </div>
                            )}

                            {/* Bottom Footer Actions */}
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    gap: 12,
                                    paddingTop: 12,
                                    borderTop: '1px solid #f1f5f9'
                                }}
                            >
                                <SecondaryButton
                                    onClick={onClose}
                                    style={{
                                        minWidth: 100,
                                        textTransform: 'uppercase',
                                        fontWeight: 600,
                                        fontSize: 12
                                    }}
                                >
                                    Cancel
                                </SecondaryButton>
                                {!disabled && (
                                    <PrimaryButton
                                        onClick={submitImage}
                                        disabled={!state.showSubmit && imageList.length === 0}
                                        isBtnLoader={loader}
                                        style={{
                                            minWidth: 110,
                                            textTransform: 'uppercase',
                                            fontWeight: 600,
                                            fontSize: 12
                                        }}
                                    >
                                        Save Photos
                                    </PrimaryButton>
                                )}
                            </div>
                        </div>
                            </DialogComp>
                        )}
                    </>
                );
            }}
        </ImageUploading>
    );
};

export default ImageUpload;
